/**
 * TenantCreateModal — minimal "+ Tenant toevoegen" form.
 *
 * Backend's POST /tenants accepts a full TenantCreateIn payload but
 * everything beyond slug + name has a sensible default (weekly/monthly
 * enabled, Europe/Brussels timezone, etc.). The advanced settings are
 * editable later on the SettingsScreen.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { ApiError } from '@/lib/api';
import { apiPost } from '@/features/_shared/queries';
import { toast } from '@/lib/toast';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (slug: string) => void;
}

interface TenantCreated {
  id: string;
  slug: string;
  name: string;
}

const SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

export function TenantCreateModal({ open, onClose, onCreated }: Props): JSX.Element | null {
  const qc = useQueryClient();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSlug('');
      setName('');
      setContactEmail('');
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      apiPost<TenantCreated>(
        '/tenants',
        {
          slug,
          name,
          contact_email: contactEmail || null,
        },
        { tenant: 'all' },
      ),
    onSuccess: (created) => {
      toast({ tone: 'success', message: `Tenant '${created.name}' aangemaakt.` });
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      onCreated?.(created.slug);
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.problem.detail ?? err.problem.title ?? 'Aanmaken mislukt.');
      } else {
        setError('Onbekende fout bij aanmaken.');
      }
    },
  });

  if (!open) return null;

  const slugValid = SLUG_RE.test(slug);
  const canSubmit = !mutation.isPending && slugValid && name.trim().length > 1;

  const handleSubmit = (evt: FormEvent<HTMLFormElement>): void => {
    evt.preventDefault();
    setError(null);
    if (!canSubmit) return;
    mutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-label="Tenant toevoegen"
      onClick={onClose}
    >
      <div
        className="w-[440px] max-w-[95vw] rounded-md bg-white shadow-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-[14px] font-semibold text-slate-900">Tenant toevoegen</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="text-slate-400 hover:text-slate-900"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-4 text-[13px]">
          <div>
            <label className="mb-1 block font-medium text-slate-700" htmlFor="tenant-slug">
              Slug
            </label>
            <input
              id="tenant-slug"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="acme-corp"
              required
              autoFocus
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 font-mono text-[12px] outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Kleine letters, cijfers, koppeltekens. Wordt onderdeel van de URL en het
              RUA-ingestadres <span className="font-mono">reports@&lt;slug&gt;.&lt;ingest-domein&gt;</span>{' '}
              (het exacte adres staat na aanmaken op de tenant-pagina).
            </p>
          </div>

          <div>
            <label className="mb-1 block font-medium text-slate-700" htmlFor="tenant-name">
              Naam
            </label>
            <input
              id="tenant-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Corporation"
              required
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
            />
          </div>

          <div>
            <label className="mb-1 block font-medium text-slate-700" htmlFor="tenant-contact">
              Contact-e-mail <span className="font-normal text-slate-400">(optioneel)</span>
            </label>
            <input
              id="tenant-contact"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="security@acme.test"
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
            />
          </div>

          {error && (
            <div
              role="alert"
              className="rounded border border-fail-100 bg-fail-50 px-2.5 py-1.5 text-[12px] text-fail-700"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 hover:bg-slate-50"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded bg-brand-600 px-3 py-1.5 font-medium text-slate-900 hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending ? 'Bezig…' : 'Aanmaken'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
